/**
 * TaskChat - Chat interface for task interaction.
 *
 * Displays message list and input field for sending messages.
 */

import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  List,
  ListItem,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { colors } from '../../theme/colors';

export interface ChatMessage {
  id: string;
  role: 'USER' | 'ASSISTANT' | 'SYSTEM';
  content: string;
  timestamp: string;
}

export interface TaskChatProps {
  messages: ChatMessage[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  isSending?: boolean;
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
  maxHeight?: number | string;
  showTimestamps?: boolean;
}

export const TaskChat: React.FC<TaskChatProps> = ({
  messages,
  inputValue,
  onInputChange,
  onSend,
  isSending = false,
  placeholder = 'Type your message...',
  disabled = false,
  compact = false,
  maxHeight = 400,
  showTimestamps = true,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (inputValue.trim() && !isSending && !disabled) {
        onSend();
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: compact ? 1.5 : 2,
          bgcolor: colors.gray[50],
          maxHeight,
        }}
      >
        <List sx={{ p: 0 }}>
          {messages.map((msg) => (
            <ListItem
              key={msg.id}
              sx={{
                flexDirection: 'column',
                alignItems: msg.role === 'USER' ? 'flex-end' : 'flex-start',
                px: 0,
                py: compact ? 0.5 : 0.75,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: compact ? 1 : 1.5,
                  maxWidth: '80%',
                  bgcolor: msg.role === 'USER' ? 'primary.main' : 'background.paper',
                  color: msg.role === 'USER' ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 2,
                  border: msg.role === 'USER' ? 'none' : 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: compact ? '0.85rem' : '0.875rem',
                  }}
                >
                  {msg.content}
                </Typography>
              </Paper>
              {showTimestamps && !compact && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1 }}>
                  {new Date(msg.timestamp).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Typography>
              )}
            </ListItem>
          ))}
        </List>
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box
        sx={{
          p: compact ? 1 : 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline={!compact}
            maxRows={compact ? 1 : 4}
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={disabled || isSending}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: compact ? 2 : 3,
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={onSend}
            disabled={!inputValue.trim() || isSending || disabled}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              width: compact ? 36 : 44,
              height: compact ? 36 : 44,
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: colors.gray[300], color: 'white' },
            }}
          >
            {isSending ? (
              <CircularProgress size={compact ? 18 : 22} color="inherit" />
            ) : (
              <SendIcon sx={{ fontSize: compact ? 18 : 22 }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default TaskChat;
